import React, { useMemo } from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const COLORS = {
  text: '#111111',
  subtext: '#555555',
  border: '#D9D9D9',
  lightBorder: '#EDEDED',
  headerBg: '#F4F4F4',
  tableHeaderBg: '#EDEDED',
  zebra: '#FAFAFA',
  black: '#000000',
  white: '#FFFFFF',
  grayDark: '#222222',
  grayMid: '#666666',
};

const FONT_SIZES = {
  title: 16,
  h1: 13,
  h2: 11,
  body: 9,
  small: 8,
};

const FIRST_PAGE_MAX_ITEM_ROWS = 10;

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: COLORS.white,
    padding: 20,
    fontSize: FONT_SIZES.body,
    fontFamily: 'Helvetica',
    color: COLORS.text,
  },
  invoiceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    backgroundColor: COLORS.headerBg,
    border: `1px solid ${COLORS.border}`,
    padding: 12,
    marginBottom: 16,
  },
  companySection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
    gap: 10,
  },
  companyLogo: {
    width: 80,
    height: 80,
  },
  companyInfo: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    flex: 1,
  },
  companyName: {
    fontSize: FONT_SIZES.title,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 4,
    color: COLORS.grayDark,
  },
  companyAddress: {
    fontSize: FONT_SIZES.body,
    color: COLORS.subtext,
    lineHeight: 1.4,
  },
  invoiceInfoSection: {
    width: 220,
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  taxInvoiceBadge: {
    backgroundColor: COLORS.black,
    color: COLORS.white,
    padding: 6,
    fontSize: FONT_SIZES.body,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    width: 120,
  },
  originalCopyText: {
    border: `1px solid ${COLORS.border}`,
    padding: 4,
    textAlign: 'center',
    fontSize: FONT_SIZES.small,
    color: COLORS.grayMid,
    width: 140,
    marginTop: 6,
  },
  invoiceMeta: {
    marginTop: 8,
    width: 200,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2,
    borderBottom: `1px solid ${COLORS.lightBorder}`,
  },
  metaLabel: {
    fontSize: FONT_SIZES.small,
    color: COLORS.subtext,
  },
  metaValue: {
    fontSize: FONT_SIZES.body,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.text,
  },
  threeColumnSection: {
    flexDirection: 'row',
    border: `1px solid ${COLORS.border}`,
    marginBottom: 16,
  },
  column: {
    flex: 1,
    padding: 12,
    borderRight: `1px solid ${COLORS.border}`,
  },
  lastColumn: {
    flex: 1,
    padding: 12,
  },
  sectionHeader: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.h2,
    color: COLORS.grayDark,
    marginBottom: 8,
    borderBottom: `1px solid ${COLORS.lightBorder}`,
    paddingBottom: 4,
    textTransform: 'uppercase',
  },
  detailItem: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  detailLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.small,
    color: COLORS.subtext,
    width: 70,
  },
  detailValue: {
    fontSize: FONT_SIZES.body,
    color: COLORS.text,
    flex: 1,
  },
  table: {
    width: '100%',
    border: `1px solid ${COLORS.border}`,
    marginBottom: 16,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: COLORS.tableHeaderBg,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  tableHeaderCell: {
    padding: 8,
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.body,
    color: COLORS.grayDark,
    textAlign: 'center',
    borderRight: `1px solid ${COLORS.border}`,
  },
  tableHeaderCellLast: {
    padding: 8,
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.body,
    color: COLORS.grayDark,
    textAlign: 'center',
    borderRight: 'none',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: `1px solid ${COLORS.lightBorder}`,
    minHeight: 24,
  },
  tableRowEven: {
    backgroundColor: COLORS.zebra,
  },
  tableCell: {
    padding: 6,
    fontSize: FONT_SIZES.body,
    textAlign: 'center',
    borderRight: `1px solid ${COLORS.border}`,
    justifyContent: 'center',
  },
  tableCellLast: {
    padding: 6,
    fontSize: FONT_SIZES.body,
    textAlign: 'center',
    borderRight: 'none',
    justifyContent: 'center',
  },
  tableCellLeft: {
    textAlign: 'left',
  },
  tableCellRight: {
    textAlign: 'right',
  },
  productName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.body,
    marginBottom: 2,
    color: COLORS.grayDark,
  },
  productDesc: {
    fontSize: FONT_SIZES.small,
    color: COLORS.subtext,
    lineHeight: 1.2,
  },
  totalRow: {
    backgroundColor: COLORS.tableHeaderBg,
  },
  totalRowCell: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.h2,
    padding: 8,
    textAlign: 'right',
    color: COLORS.grayDark,
  },
  totalWords: {
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.white,
    padding: 10,
    marginBottom: 12,
  },
  wordsLabel: {
    fontSize: FONT_SIZES.body,
    color: COLORS.text,
  },
  bottomSection: {
    flexDirection: 'row',
    gap: 12,
  },
  leftColumnBottom: {
    flex: 1,
    flexDirection: 'column',
    gap: 10,
  },
  rightColumnBottom: {
    width: 260,
    flexDirection: 'column',
    gap: 10,
  },
  bankCard: {
    border: `1px solid ${COLORS.border}`,
    padding: 12,
    backgroundColor: COLORS.white,
  },
  cardTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.h2,
    color: COLORS.grayDark,
    borderBottom: `1px solid ${COLORS.lightBorder}`,
    paddingBottom: 4,
    marginBottom: 8,
  },
  bankRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  bankLabel: {
    fontSize: FONT_SIZES.small,
    color: COLORS.subtext,
    width: 65,
  },
  bankValue: {
    fontSize: FONT_SIZES.body,
    color: COLORS.text,
    flex: 1,
  },
  bankGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  bankColLeft: {
    flex: 1,
  },
  bankColRight: {
    width: 110,
    alignItems: 'center',
  },
  bankQrWrap: {
    marginTop: 0,
    alignItems: 'center',
  },
  qrImage: {
    width: 90,
    height: 90,
    border: `1px solid ${COLORS.border}`,
  },
  termsCard: {
    border: `1px solid ${COLORS.border}`,
    padding: 12,
    backgroundColor: COLORS.white,
  },
  termsListItem: {
    fontSize: FONT_SIZES.body,
    color: COLORS.text,
    marginBottom: 3,
  },
  summaryCard: {
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.white,
    padding: 12,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
    borderBottom: `1px solid ${COLORS.lightBorder}`,
  },
  summaryLabel: {
    fontSize: FONT_SIZES.body,
    color: COLORS.text,
  },
  summaryValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.body,
    color: COLORS.grayDark,
  },
  finalTotal: {
    backgroundColor: COLORS.black,
    color: COLORS.white,
    marginTop: 8,
    padding: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  finalTotalLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.h2,
    color: COLORS.white,
  },
  finalTotalValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.h2,
    color: COLORS.white,
  },
});

// Utility functions
const sanitizeQr = (src) => {
  if (!src || typeof src !== "string") return null;
  if (src.startsWith("data:image")) return src.trim();
  if (/^[A-Za-z0-9+/=]+$/.test(src)) return "data:image/png;base64," + src;
  return null;
};

const numberToWords = (num) => {
  const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE'];
  const teens = ['TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
  const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

  const convertHundreds = (n) => {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' HUNDRED ';
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    } else if (n >= 10) {
      result += teens[n - 10] + ' ';
      n = 0;
    }
    if (n > 0) result += ones[n] + ' ';
    return result;
  };

  const convertLargeNumber = (n) => {
    if (n === 0) return '';
    let result = '';
    const crores = Math.floor(n / 10000000);
    n %= 10000000;
    const lakhs = Math.floor(n / 100000);
    n %= 100000;
    const thousands = Math.floor(n / 1000);
    n %= 1000;

    if (crores > 0) result += convertHundreds(crores) + 'CRORE ';
    if (lakhs > 0) result += convertHundreds(lakhs) + 'LAKH ';
    if (thousands > 0) result += convertHundreds(thousands) + 'THOUSAND ';
    if (n > 0) result += convertHundreds(n);

    return result;
  };

  const integerPart = Math.floor(num);
  const decimalPart = Math.round((num - integerPart) * 100);
  let result = integerPart === 0 ? 'ZERO' : convertLargeNumber(integerPart).trim();

  result += ' RUPEES';
  if (decimalPart > 0) {
    result += ' AND ' + convertHundreds(decimalPart).trim() + ' PAISE';
  }
  result += ' ONLY';
  return result;
};

const formatIndianDate = (dateString) => {
  if (!dateString) return new Date().toLocaleDateString('en-IN');
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) return date.toLocaleDateString('en-IN');
  const parts = (dateString || '').split(/[-/]/);
  if (parts.length === 3) {
    const year = parts[0].length === 4 ? parts[0] : parts[2];
    const month = parts[1];
    const day = parts.length === 4 ? parts[2] : parts[0];
    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
  }
  return new Date().toLocaleDateString('en-IN');
};

const parseNumber = (val) => typeof val === 'string' ? (parseFloat(val) || 0) : (val || 0);

const InvoicePDF = React.memo(({
  invoiceData,
  items,
  localSummary,
  getLocalItemTotal,
  calculateLocalItemTax,
  showTaxPerItemColumn,
  showHsnColumn,
  hasTax,
  hasdiscount,
  discount,
  taxType,
  sgstRate,
  cgstRate,
  igstRate,
  selectedBank,
  qrCodeDataURL,
  showValueColumn,
}) => {
  const { fromInfo, selectedClient, newClient, invoiceInfo, termsAndConditions, userProfile } = invoiceData;

  // Calculate column configuration
  const columnConfig = useMemo(() => {
    const base = [
      { key: 'sno', width: 7 },
      { key: 'description', width: 36 },
      { key: 'qty', width: 9 },
      { key: 'rate', width: 12 },
    ];
    const opt = [];
    
    if (showHsnColumn) opt.push({ key: 'hsn', width: 10 });
    
    if (!hasTax && !hasdiscount) {
      opt.push({ key: 'total', width: 14 });
    } else if (!hasTax && hasdiscount) {
      opt.push({ key: 'subtotal', width: 14 });
    } else if (hasTax && !hasdiscount) {
      opt.push({ key: 'tax', width: 10 });
      opt.push({ key: 'total', width: 14 });
    } else {
      opt.push({ key: 'subtotal', width: 14 });
    }
    
    const allColumns = [...base, ...opt];
    const usedWidth = allColumns.reduce((sum, c) => sum + c.width, 0);
    const remaining = 100 - usedWidth;
    
    const widths = {};
    allColumns.forEach((c) => {
      widths[c.key] = c.key === 'description' ? c.width + remaining : c.width;
    });
    
    const lastColumnKey = hasdiscount ? 'subtotal' : 'total';
    const totalSpanWidth = 100 - (widths[lastColumnKey] || 14);
    
    return { widths, lastColumnKey, totalSpanWidth };
  }, [showHsnColumn, hasTax, hasdiscount]);

  // Calculate financial summary
  const financialSummary = useMemo(() => {
    const validItems = items.filter((it) => it.product_name);
    let subtotal = 0;
    validItems.forEach(item => {
      const qty = parseNumber(item.quantity);
      const price = parseNumber(item.unit_price);
      subtotal += qty * price;
    });

    let taxableValue = subtotal;
    let discountAmount = 0;
    
    if (hasdiscount && discount > 0) {
      discountAmount = subtotal * (discount / 100);
      taxableValue = subtotal - discountAmount;
    }

    let sgstTotal = 0, cgstTotal = 0, igstTotal = 0, totalTax = 0;
    
    if (hasTax) {
      if (taxType === 'SGST_CGST') {
        sgstTotal = taxableValue * (sgstRate / 100);
        cgstTotal = taxableValue * (cgstRate / 100);
        totalTax = sgstTotal + cgstTotal;
      } else if (taxType === 'IGST') {
        igstTotal = taxableValue * (igstRate / 100);
        totalTax = igstTotal;
      }
    }

    const finalTotal = taxableValue + totalTax;

    return {
      subtotal,
      taxableValue,
      discountAmount,
      sgstTotal,
      cgstTotal,
      igstTotal,
      totalTax,
      finalTotal,
    };
  }, [items, hasTax, hasdiscount, discount, taxType, sgstRate, cgstRate, igstRate]);

  const validItems = items.filter((it) => it.product_name);
  const page1Items = validItems.slice(0, FIRST_PAGE_MAX_ITEM_ROWS);
  const remainingItems = validItems.slice(FIRST_PAGE_MAX_ITEM_ROWS);
  const hasSecondPage = remainingItems.length > 0;

  const renderTableHeader = () => (
    <View style={styles.tableHeader}>
      <Text style={[styles.tableHeaderCell, { width: `${columnConfig.widths.sno}%` }]}>S.No</Text>
      <Text style={[styles.tableHeaderCell, { width: `${columnConfig.widths.description}%` }]}>Name of Product / Service</Text>
      {showHsnColumn && (
        <Text style={[styles.tableHeaderCell, { width: `${columnConfig.widths.hsn}%` }]}>HSN/SAC</Text>
      )}
      <Text style={[styles.tableHeaderCell, { width: `${columnConfig.widths.qty}%` }]}>Qty</Text>
      <Text style={[styles.tableHeaderCell, { width: `${columnConfig.widths.rate}%` }]}>Rate</Text>
      
      {!hasTax && !hasdiscount && (
        <Text style={[styles.tableHeaderCellLast, { width: `${columnConfig.widths.total}%` }]}>Total</Text>
      )}
      {!hasTax && hasdiscount && (
        <Text style={[styles.tableHeaderCellLast, { width: `${columnConfig.widths.subtotal}%` }]}>Subtotal</Text>
      )}
      {hasTax && !hasdiscount && (
        <>
          <Text style={[styles.tableHeaderCell, { width: `${columnConfig.widths.tax}%` }]}>Tax</Text>
          <Text style={[styles.tableHeaderCellLast, { width: `${columnConfig.widths.total}%` }]}>Total</Text>
        </>
      )}
      {hasTax && hasdiscount && (
        <Text style={[styles.tableHeaderCellLast, { width: `${columnConfig.widths.subtotal}%` }]}>Subtotal</Text>
      )}
    </View>
  );

  const renderTableRow = (item, index, startIndex) => {
    const base = getLocalItemTotal(item);
    const itemTax = calculateLocalItemTax(item);
    const qty = parseNumber(item.quantity);
    const price = parseNumber(item.unit_price);

    return (
      <View
        key={item.stableKey || `item-${startIndex + index}`}
        style={[styles.tableRow, index % 2 === 1 && styles.tableRowEven]}
      >
        <Text style={[styles.tableCell, { width: `${columnConfig.widths.sno}%` }]}>{startIndex + index + 1}</Text>
        <View style={[styles.tableCell, styles.tableCellLeft, { width: `${columnConfig.widths.description}%` }]}>
          <Text style={styles.productName}>{item.product_name}</Text>
          {!!item.description && <Text style={styles.productDesc}>{item.description}</Text>}
        </View>
        {showHsnColumn && (
          <Text style={[styles.tableCell, { width: `${columnConfig.widths.hsn}%` }]}>
            {item.hsn_code || item.hsn || '-'}
          </Text>
        )}
        <Text style={[styles.tableCell, { width: `${columnConfig.widths.qty}%` }]}>{qty}</Text>
        <Text style={[styles.tableCell, { width: `${columnConfig.widths.rate}%` }]}>{price.toFixed(2)}</Text>
        
        {!hasTax && !hasdiscount && (
          <Text style={[styles.tableCell, styles.tableCellRight, { width: `${columnConfig.widths.total}%` }]}>
            {base.toFixed(2)}
          </Text>
        )}
        {!hasTax && hasdiscount && (
          <Text style={[styles.tableCell, styles.tableCellRight, { width: `${columnConfig.widths.subtotal}%` }]}>
            {base.toFixed(2)}
          </Text>
        )}
        {hasTax && !hasdiscount && (
          <>
            <Text style={[styles.tableCell, { width: `${columnConfig.widths.tax}%` }]}>{itemTax.toFixed(2)}</Text>
            <Text style={[styles.tableCell, styles.tableCellRight, { width: `${columnConfig.widths.total}%` }]}>
              {(base + itemTax).toFixed(2)}
            </Text>
          </>
        )}
        {hasTax && hasdiscount && (
          <Text style={[styles.tableCell, styles.tableCellRight, { width: `${columnConfig.widths.subtotal}%` }]}>
            {base.toFixed(2)}
          </Text>
        )}
      </View>
    );
  };

  const ItemsTable = ({ rows, startIndex = 0 }) => (
    <View style={styles.table}>
      {renderTableHeader()}
      {rows.map((item, index) => renderTableRow(item, index, startIndex))}
    </View>
  );

  const calculateRowTotal = (rows) => {
    return rows.reduce((sum, item) => {
      const b = getLocalItemTotal(item);
      const t = (hasTax && !hasdiscount) ? calculateLocalItemTax(item) : 0;
      return sum + b + t;
    }, 0);
  };

  const TotalRow = ({ rows }) => (
    <View style={[styles.tableRow, styles.totalRow]}>
      <Text style={[styles.totalRowCell, { width: `${columnConfig.totalSpanWidth}%` }]}>Total</Text>
      <Text style={[styles.totalRowCell, { width: `${columnConfig.widths[columnConfig.lastColumnKey]}%` }]}>
        {calculateRowTotal(rows).toFixed(2)}
      </Text>
    </View>
  );

  const BottomBlocks = () => (
    <View>
      <View style={styles.totalWords}>
        <Text style={styles.wordsLabel}>
          Total in words: {numberToWords(financialSummary.finalTotal)}
        </Text>
      </View>
      <View style={styles.bottomSection}>
        <View style={styles.leftColumnBottom}>
          {selectedBank && (
            <View style={styles.bankCard}>
              <Text style={styles.cardTitle}>Bank Details</Text>
              <View style={styles.bankGrid}>
                <View style={styles.bankColLeft}>
                  <View style={styles.bankRow}>
                    <Text style={styles.bankLabel}>Bank Name:</Text>
                    <Text style={styles.bankValue}>{selectedBank.bank_name}</Text>
                  </View>
                  <View style={styles.bankRow}>
                    <Text style={styles.bankLabel}>A/C Number:</Text>
                    <Text style={styles.bankValue}>{selectedBank.account_number}</Text>
                  </View>
                  <View style={styles.bankRow}>
                    <Text style={styles.bankLabel}>IFSC Code:</Text>
                    <Text style={styles.bankValue}>{selectedBank.ifsc_code}</Text>
                  </View>
                  {!!selectedBank.account_holder_name && (
                    <View style={styles.bankRow}>
                      <Text style={styles.bankLabel}>A/C Holder:</Text>
                      <Text style={styles.bankValue}>{selectedBank.account_holder_name}</Text>
                    </View>
                  )}
                  {!!selectedBank.upi_id && (
                    <View style={styles.bankRow}>
                      <Text style={styles.bankLabel}>UPI ID:</Text>
                      <Text style={styles.bankValue}>{selectedBank.upi_id}</Text>
                    </View>
                  )}
                </View>
                <View style={styles.bankColRight}>
                  {selectedBank && qrCodeDataURL && financialSummary.finalTotal <= 100000 && (
                    <View style={styles.bankQrWrap}>
                      <Image src={sanitizeQr(qrCodeDataURL)} style={styles.qrImage} />
                      <Text style={styles.bankLabel}>Scan to pay via UPI</Text>
                    </View>
                  )}
                </View>
              </View>
            </View>
          )}
          <View style={styles.termsCard}>
            <Text style={styles.cardTitle}>Terms & Conditions</Text>
            {termsAndConditions && termsAndConditions.filter(t => t.trim() !== '').length > 0 ? (
              termsAndConditions
                .filter(t => t.trim() !== '')
                .map((t, i) => <Text key={`term-${i}`} style={styles.termsListItem}>• {t}</Text>)
            ) : (
              <Text style={styles.termsListItem}>• Payment due within 30 days.</Text>
            )}
          </View>
        </View>
        <View style={styles.rightColumnBottom}>
          <View style={styles.summaryCard}>
            {hasdiscount && financialSummary.discountAmount > 0 && (
              <>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Subtotal</Text>
                  <Text style={styles.summaryValue}>Rs.{financialSummary.subtotal.toFixed(2)}</Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Less: Discount ({discount}%)</Text>
                  <Text style={styles.summaryValue}>Rs.{financialSummary.discountAmount.toFixed(2)}</Text>
                </View>
              </>
            )}
            
            {hasTax && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Taxable Value</Text>
                <Text style={styles.summaryValue}>Rs.{financialSummary.taxableValue.toFixed(2)}</Text>
              </View>
            )}
            
            {hasTax && taxType === 'SGST_CGST' && (
              <>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>CGST ({cgstRate}%)</Text>
                  <Text style={styles.summaryValue}>Rs.{financialSummary.cgstTotal.toFixed(2)}</Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>SGST ({sgstRate}%)</Text>
                  <Text style={styles.summaryValue}>Rs.{financialSummary.sgstTotal.toFixed(2)}</Text>
                </View>
              </>
            )}
            {hasTax && taxType === 'IGST' && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>IGST ({igstRate}%)</Text>
                <Text style={styles.summaryValue}>Rs.{financialSummary.igstTotal.toFixed(2)}</Text>
              </View>
            )}
            {hasTax && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Total Tax</Text>
                <Text style={styles.summaryValue}>Rs.{financialSummary.totalTax.toFixed(2)}</Text>
              </View>
            )}
            
            <View style={styles.finalTotal}>
              <Text style={styles.finalTotalLabel}>Total Amount (Rs)</Text>
              <Text style={styles.finalTotalValue}>{financialSummary.finalTotal.toFixed(2)}</Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );

  const clientInfo = selectedClient || newClient;
  const clientName = selectedClient ? selectedClient.client_name : (newClient?.name || 'Customer Name');
  const clientCompany = selectedClient ? selectedClient.client_company_name : newClient?.company;
  const clientAddress = selectedClient ? selectedClient.address : newClient?.address;
  const clientEmail = selectedClient ? (selectedClient.email || selectedClient.client_email) : newClient?.email;
  const clientGST = selectedClient ? selectedClient.client_gst_number : (newClient?.gst || '—');

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Invoice header */}
        <View style={styles.invoiceHeader}>
          <View style={styles.companySection}>
            {userProfile?.logo_url && <Image style={styles.companyLogo} src={userProfile.logo_url} />}
            <View style={styles.companyInfo}>
              <Text style={styles.companyName}>{fromInfo?.company_name || fromInfo?.name || 'Company Name'}</Text>
              {!!fromInfo?.address && <Text style={styles.companyAddress}>{fromInfo.address}</Text>}
              {!!fromInfo?.phone && <Text style={styles.companyAddress}>Phone: {fromInfo.phone}</Text>}
              {!!(fromInfo?.contact_email || fromInfo?.email) && (
                <Text style={styles.companyAddress}>Email: {fromInfo.contact_email || fromInfo.email}</Text>
              )}
              {!!fromInfo?.gst_number && <Text style={styles.companyAddress}>GSTIN: {fromInfo.gst_number}</Text>}
            </View>
          </View>
          <View style={styles.invoiceInfoSection}>
            <Text style={styles.taxInvoiceBadge}>INVOICE</Text>
            <Text style={styles.originalCopyText}>ORIGINAL FOR RECIPIENT</Text>
            <View style={styles.invoiceMeta}>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>Invoice No.</Text>
                <Text style={styles.metaValue}>{invoiceInfo.number}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>Invoice Date</Text>
                <Text style={styles.metaValue}>{formatIndianDate(invoiceInfo.issueDate)}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>Due Date</Text>
                <Text style={styles.metaValue}>{formatIndianDate(invoiceInfo.dueDate)}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Three-column section */}
        <View style={styles.threeColumnSection}>
          <View style={styles.column}>
            <Text style={styles.sectionHeader}>Seller</Text>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Name:</Text>
              <Text style={styles.detailValue}>{fromInfo?.company_name || fromInfo?.name || 'Company Name'}</Text>
            </View>
            {!!fromInfo?.address && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Address:</Text>
                <Text style={styles.detailValue}>{fromInfo.address}</Text>
              </View>
            )}
            {!!fromInfo?.phone && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Phone:</Text>
                <Text style={styles.detailValue}>{fromInfo.phone}</Text>
              </View>
            )}
            {!!(fromInfo?.contact_email || fromInfo?.email) && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Email:</Text>
                <Text style={styles.detailValue}>{fromInfo.contact_email || fromInfo.email}</Text>
              </View>
            )}
            {!!fromInfo?.gst_number && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>GSTIN:</Text>
                <Text style={styles.detailValue}>{fromInfo.gst_number}</Text>
              </View>
            )}
          </View>
          <View style={styles.column}>
            <Text style={styles.sectionHeader}>Buyer</Text>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Name:</Text>
              <Text style={styles.detailValue}>{clientName}</Text>
            </View>
            {!!clientCompany && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Company:</Text>
                <Text style={styles.detailValue}>{clientCompany}</Text>
              </View>
            )}
            {!!clientAddress && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Address:</Text>
                <Text style={styles.detailValue}>{clientAddress}</Text>
              </View>
            )}
            {!!clientEmail && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Email:</Text>
                <Text style={styles.detailValue}>{clientEmail}</Text>
              </View>
            )}
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>GSTIN:</Text>
              <Text style={styles.detailValue}>{clientGST}</Text>
            </View>
          </View>
          <View style={styles.lastColumn}>
            <Text style={styles.sectionHeader}>Invoice Details</Text>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Invoice No.</Text>
              <Text style={styles.detailValue}>{invoiceInfo.number}</Text>
            </View>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Invoice Date</Text>
              <Text style={styles.detailValue}>{formatIndianDate(invoiceInfo.issueDate)}</Text>
            </View>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Due Date</Text>
              <Text style={styles.detailValue}>{formatIndianDate(invoiceInfo.dueDate)}</Text>
            </View>
          </View>
        </View>

        <ItemsTable rows={page1Items} startIndex={0} />
        <TotalRow rows={page1Items} />
        {!hasSecondPage && <BottomBlocks />}
      </Page>

      {hasSecondPage && (
        <Page size="A4" style={styles.page}>
          <ItemsTable rows={remainingItems} startIndex={FIRST_PAGE_MAX_ITEM_ROWS} />
          <TotalRow rows={remainingItems} />
          <BottomBlocks />
        </Page>
      )}
    </Document>
  );
});

InvoicePDF.displayName = 'InvoicePDF';
export default InvoicePDF;